import { customElement, property, query } from 'lit/decorators.js';

import type { PermutiveCommerceAffiliateData } from './definitions/types';
import { extractAsinFromUrl, extractAscTagFromUrl, getUtmParams } from './util';
import { buildMappedValueObject, decorateUrlUsingMapper } from '@themaven-net/commerce-shared';
import type {
    CommerceItem,
    CommerceKeyValueMapper,
    CommerceObjectValueMapper,
    TrackonomicsWrapperConfig,
} from '@themaven-net/commerce-shared';
import { BaseCommerceLink } from '@themaven-net/commerce-published-webcomponents';

@customElement('phoenix-commerce-link')
export default class PhoenixCommerceLink extends BaseCommerceLink {
    @property()
    public linkWrapDelay: number = 1000;

    @property()
    public ncaLink?: string;

    @query('a.m-product--commerce-link')
    anchor: HTMLAnchorElement | undefined;

    protected unalteredUrl?: string;
    protected amazonDetailTag?: string;
    protected trackonomicsDetails?: TrackonomicsWrapperConfig;
    protected linkWrapTimeoutSymbol?: ReturnType<typeof setTimeout> | true;

    protected handleLinkClick(clickEvent: Event): void {
        if (!(clickEvent.target instanceof HTMLAnchorElement)) {
            return;
        }

        this.sendClickAnalytics(clickEvent);
    }

    protected override selectOffers(item?: CommerceItem): void {
        super.selectOffers(item);
        // cache so NCA doesn't alter our ASIN stuff.
        this.unalteredUrl = this.offerSelection?.primaryOffer?.url;
    }

    protected override retrieveEnvironmentSettings(): void {
        this.amazonDetailTag = window.phxCommerce.perPageDetails?.amazonDetailTag;
        if (window.phxCommerce.wrappersEnabled.trackonomics === true) {
            this.pageLevelWrapperConfigs['Trackonomics'] = {
                vendor: 'Trackonomics',
                options: window.phxCommerce.trackonomics.options ?? undefined,
            };
        }
    }

    protected override buildDecoratedHref(): string {
        if (this.primaryOffer?.url === undefined) {
            super.selectOffers();
            this.wrapHref();
        }

        if (this.wrappedHref?.includes('clicks.trx-hub')) {
            return decorateUrlUsingMapper(this.wrappedHref, this.trackonomicsKeyValueMapper);
        }

        return super.buildDecoratedHref();
    }

    protected trackonomicsKeyValueMapper: CommerceKeyValueMapper<string> = {
        ref: () => {
            return document.referrer;
        },
        exp: () => {
            return document.body.getAttribute('experiments') ?? '';
        },
        mcor: () => {
            return window.mavenCorrelator?.toString() ?? '0';
        },
        ...getUtmParams(),
    };

    /**
     * Builds an object to send to permutive
     */
    protected permutiveKeyValueMapper: CommerceObjectValueMapper<PermutiveCommerceAffiliateData> = {
        pageUrl: () => window.location.toString(),
        product: () => {
            const built: PermutiveCommerceAffiliateData['product'] = { productUrl: this.href };
            const item = this.item;
            const offer = this.offerSelection?.primaryOffer;

            if (item) {
                built.name = item.title;
            }

            if (offer) {
                built.price = {
                    value: Number(offer.price.substring(1)),
                    currency: 'USD',
                };
            }

            return built;
        },
    };

    /**
     * These are added as data attributes to the anchor
     */
    protected getDynamicAttributesMapper(): CommerceKeyValueMapper<string> {
        return {
            'data-asin': () => extractAsinFromUrl(this.unalteredUrl ?? ''),
            'data-aps-asin': () => extractAsinFromUrl(this.unalteredUrl ?? ''),
            'data-aps-asc-tag': () => extractAscTagFromUrl(this.unalteredUrl ?? ''), // @todo handle dynamic tag replacement
        };
    }

    protected sendClickAnalytics(clickEvent: Event): void {
        window.phoenixTrackClickEvent(clickEvent.target, clickEvent);

        if (typeof window.permutive?.track === 'function') {
            window.permutive.track(
                'AffiliateLinkClick',
                buildMappedValueObject(this.permutiveKeyValueMapper)
            );
        }
    }

    /**
     * Overriding to add a delay to link wrapping to allow ACS to inspect and update the url
     */
    protected override wrapHref(_url?: string): void {
        if (this.linkWrapTimeoutSymbol === true) {
            return;
        }

        if (this.linkWrapTimeoutSymbol !== undefined) {
            // just in case this is coming from a property change, clear the timeout to prevent rerender
            clearTimeout(this.linkWrapTimeoutSymbol);
            this.linkWrapTimeoutSymbol = true;
        }

        // if we have a link from NCA, go ahead and render immediately
        if (this.ncaLink !== undefined && this.offerSelection?.primaryOffer) {
            // hackety hack hack.  Working around super.wrapLink() not having a passed in param.
            // do better in raven
            this.offerSelection.primaryOffer.url = this.ncaLink;
            super.wrapHref();
            this.requestUpdate();
            return;
        }

        // wait for nca to inspect the element
        this.linkWrapTimeoutSymbol = setTimeout(() => {
            this.linkWrapTimeoutSymbol = true;
            super.wrapHref();
            this.requestUpdate();
        }, this.linkWrapDelay);
    }

    protected override getClassnames(): string {
        return super.getClassnames() + ' noskim';
    }
}
